import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { debounceTime, lastValueFrom, take } from 'rxjs';
import {
  BookingCreateUpdateModel,
  BookingModel,
  CourseParticipantCreateUpdateModel,
  CourseParticipantModel,
} from '../../../../models/booking.model';
import { TrainingCategoryModel } from '../../../../models/training-category.model';

import { CouponCodeModel } from '../../../../models/coupon.model';
import {
  SeminarConditionModel,
  SeminarConditionType,
  SeminarDateSeriesModel,
  SeminarModel,
} from '../../../../models/seminar.model';
import { AlertService } from '../../../../services/alert.service';
import { PartnerService } from '../../../../services/bed.service';
import { BookingService } from '../../../../services/booking.service';
import { CouponService } from '../../../../services/coupon.service';
import { FormService } from '../../../../services/form.service';
import { SeminarService } from '../../../../services/seminar.service';
import { UserService } from '../../../../services/user.service';
import { getCountryOptions } from '../../../../utils/address.utils';
import { getParticipantsLoadClass } from '../../../../utils/color.utils';
import {
  fieldRequired,
  getMaxLengthFromValidator,
  getTextAreaLength,
} from '../../../../utils/form.utils';
import { houseNumberValidator } from '../../../../validators/house-number.validator';

const CITY_NO_NUMBERS_PATTERN = /^[^\d]*$/;

@Component({
  selector: 'app-seminar-registration',
  templateUrl: './seminar-registration.component.html',
  styleUrls: ['./seminar-registration.component.scss'],
  standalone: false,
})
export class SeminarRegistrationComponent implements OnInit {
  constructor(
    private location: Location,
    private formBuilder: FormBuilder,
    private seminarService: SeminarService,
    private activatedRoute: ActivatedRoute,
    private formService: FormService,
    private bookingService: BookingService,
    private userService: UserService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private couponService: CouponService,
    private partnerService: PartnerService
  ) {
    this.createForm();
    this.countryOptions = getCountryOptions();
  }
  public booking: BookingModel;
  public bookingId: number;
  public groupConditionSelected: boolean = false;
  public bookingCreateModel: BookingCreateUpdateModel;

  public seminarRegistrationForm: FormGroup;
  private selectedPaymentMethod: string;
  public currentScreen:
    | 'registration'
    | 'registrationSummary'
    | 'paymentMethod'
    | 'paymentDetails' = 'registration';

  public toggleBillingAddress: boolean = true;
  public isLoading = true;
  public seminar: SeminarModel;
  public seminarCategory: TrainingCategoryModel;
  public paypalEnabled = true;
  public validCoupons: CouponCodeModel[] = [];
  public showCouponInput = true;
  private couponSubscription: any;

  public fieldRequired = fieldRequired;
  public getTextAreaLength = getTextAreaLength;
  public getMaxLengthFromValidator = getMaxLengthFromValidator;
  public getParticipantsLoadClass = getParticipantsLoadClass;
  public SeminarConditionType = SeminarConditionType;

  public minDateBirthdate = moment().subtract(100, 'years').toDate();
  public maxDateBirthdate = moment().subtract(16, 'years').toDate();
  public defaultDateBirthdate = moment().subtract(18, 'years').toDate();

  public salutationOptions = [
    { value: null, label: 'Keine' },
    { value: 'Herr', label: 'Herr' },
    { value: 'Frau', label: 'Frau' },
  ];

  public countryOptions = [];

  public async ngOnInit(): Promise<void> {
    const params = this.activatedRoute.snapshot.params;
    const paramSeminarId = params['seminarId'];
    if (!paramSeminarId) {
      this.location.back();
      return;
    }

    const paramCategoryId = params['categoryId'];

    await this.getSeminar(
      +atob(paramSeminarId),
      paramCategoryId ? +atob(paramCategoryId) : null
    );

    const paramBookingId = params['bookingId'];

    const paramDateSeriesId = params['dateSeriesId'];
    if (paramDateSeriesId) {
      this.seminarRegistrationForm.patchValue({
        dateSeriesChoice: +atob(paramDateSeriesId),
      });
    }

    await this.getCurrentUser();

    if (paramBookingId) {
      this.bookingId = +atob(paramBookingId);
      await this.getBooking(this.bookingId);
    }

    this.isLoading = false;
  }

  private async getSeminar(
    seminarId: number,
    seminarCategoryId: number
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.seminarService
        .getSeminarById(seminarId)
        .pipe(take(1))
        .subscribe({
          next: seminar => {
            this.seminar = seminar;
            if (this.navigateAwayFromDraftSeminars()) {
              return;
            }
            if (
              seminarCategoryId &&
              this.seminar.category2?.id === seminarCategoryId
            ) {
              this.seminarCategory = this.seminar.category2;
            }
            if (!this.seminarCategory) {
              this.seminarCategory = this.seminar.category1;
            }
            this.paypalEnabled = seminar.paypalPaymentAvailable;
            resolve();
          },
          error: error => {
            console.error('Error fetching course:', error);
            this.alertService.showErrorAlert(
              'Das Seminar konnte nicht geladen werden.'
            );
            reject();
          },
        });
    });
  }

  private async getBooking(bookingId: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.bookingService
        .getBookingById(bookingId)
        .pipe(take(1))
        .subscribe({
          next: booking => {
            this.booking = booking;
            this.participants.removeAt(0);

            this.booking.participants.forEach(participant => {
              this.addParticipant();
              this.participants.controls[
                this.participants.length - 1
              ].patchValue({
                ...participant,
                condition: this.convertBackendConditionName(
                  participant.condition
                ),
              });
            });

            console.log(booking);
            resolve();
          },
          error: error => {
            console.error('Error fetching booking:', error);
            reject();
          },
        });
    });
  }

  private convertBackendConditionName(condition: string): string {
    // convert condition from snake case to camel case
    return condition
      .split('_')
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join('');
  }

  private async getCurrentUser(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const userId = this.userService.currentUser$.value?.id;

      if (!userId) {
        resolve();
      }

      this.userService
        .getUserById(userId)
        .pipe(take(1))
        .subscribe({
          next: response => {
            const currentUser = response;

            this.participants.controls[0].patchValue({
              salutation: currentUser.salutation,
              jobTitle: currentUser.jobTitle,
              firstName: currentUser.firstname,
              lastName: currentUser.lastname,
              birthdate: currentUser.birthdate,
              email: currentUser.email,
              street: currentUser.address?.street,
              streetNr: currentUser.address?.houseNumber,
              zip: currentUser.address?.zipCode,
              city: currentUser.address?.city,
              country: currentUser.address?.country,
              phone: currentUser.phoneNumber,
              bedNumber: currentUser.bedMemberNumber,
            }),
              this.seminarRegistrationForm.patchValue({
                billingFirstName: currentUser.firstname,
                billingLastName: currentUser.lastname,
                billingStreet: currentUser.address?.street,
                billingStreetNr: currentUser.address?.houseNumber,
                billingZip: currentUser.address?.zipCode,
                billingCity: currentUser.address?.city,
                billingCountry: currentUser.address?.country,
                billingEmail: currentUser.email,
              });
            resolve();
          },
          error: error => {
            console.error('Error fetching user:', error);
            reject();
          },
        });
    });
  }

  private createForm(): void {
    this.seminarRegistrationForm = this.formBuilder.group({
      dateSeriesChoice: [null, Validators.required],
      participants: this.formBuilder.array([]),
      couponCode: [''],
      billingFacility: [''],
      billingFirstName: ['', Validators.required],
      billingLastName: ['', Validators.required],
      billingStreet: ['', Validators.required],
      billingStreetNr: ['', [Validators.required, houseNumberValidator()]],
      billingZip: ['', [Validators.required, Validators.minLength(3)]],
      billingCity: [
        '',
        [Validators.required, Validators.pattern(CITY_NO_NUMBERS_PATTERN)],
      ],
      billingCountry: ['', Validators.required],
      billingEmail: ['', [Validators.required, Validators.email]],
      billingPhone: [''],
      termsOfParticipation: [false, Validators.requiredTrue],
    });
    this.addParticipant();
    this.subscribeToCouponCodeInput();
  }

  get participants(): FormArray {
    return this.seminarRegistrationForm.get('participants') as FormArray;
  }

  public addParticipant(): void {
    const hasOfficeCondition = this.participants.controls.some(
      participant =>
        participant.get('condition').value === SeminarConditionType.OFFICE
    );
    const condition = hasOfficeCondition ? SeminarConditionType.OFFICE : null;
    const participant: FormGroup = this.formBuilder.group({
      id: [null],
      salutation: [''],
      jobTitle: ['', Validators.required],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      street: [''],
      streetNr: ['', houseNumberValidator()],
      zip: ['', [Validators.minLength(3)]],
      city: ['', Validators.pattern(CITY_NO_NUMBERS_PATTERN)],
      country: [''],
      birthdate: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      questions: ['', [Validators.maxLength(1200)]],
      condition: [condition, [Validators.required]],
      password: [''],
    });
    this.participants.push(participant);

    if (this.participants.length > this.freeSeats()) {
      this.alertService.showInfoAlert(
        'Für diese Veranstaltung sind nicht mehr genügend Plätze verfügbar. Sie können sich auf die Warteliste setzen lassen.',
        5000
      );
    }
  }

  public removeParticipant(index: number): void {
    if (this.participants.length === 1) {
      return;
    }
    if (this.participants.length <= 3 && this.groupConditionSelected) {
      this.groupConditionSelected = false;
      // remove group condition from participants
      this.participants.controls.forEach(participant => {
        participant.get('condition').setValue(null);
      });
    }
    this.participants.removeAt(index);
    this.cdr.detectChanges();
  }

  public calculatePrice(): number {
    let price = 0;
    this.participants.controls.forEach(participant => {
      if (participant.get('condition').value === null) {
        return;
      }
      // get price from course condition
      const conditionPrice = this.seminar.conditions.find(
        condition => condition.type === participant.get('condition').value
      )?.price;
      if (conditionPrice) {
        price += conditionPrice;
      }
    });
    return price;
  }

  public calculateDiscountedPrice(allowMinus: boolean = false): number {
    const totalPrice = this.calculatePrice();
    let price = totalPrice;
    this.validCoupons.forEach(coupon => {
      if (coupon.discountType === 'percentage') {
        price = price - totalPrice * (coupon.value / 100);
      }
      if (coupon.discountType === 'fixed') {
        price = price - coupon.value;
      }
    });
    if (!allowMinus && price < 0) {
      price = 0;
    }
    return price;
  }

  public dateConditionExpired(condition: SeminarConditionModel): boolean {
    if (!condition || !condition.date) {
      return false;
    }
    return moment().isAfter(moment(condition.date), 'day');
  }

  public conditionDisabled(condition: SeminarConditionModel): boolean {
    // Check if any participant has the office condition selected
    const hasOfficeCondition = this.participants.controls.some(
      participant =>
        participant.get('condition').value === SeminarConditionType.OFFICE
    );

    // If any participant has office condition, only office condition should be enabled
    if (hasOfficeCondition && condition.type !== SeminarConditionType.OFFICE) {
      return true;
    }

    // Disable early bird condition if the date is in the past
    if (
      condition.type === SeminarConditionType.EARLY &&
      this.dateConditionExpired(condition)
    ) {
      return true;
    }

    // All other conditions should be enabled
    return false;
  }

  public onCancel(): void {
    this.location.back();
  }

  public onConditionSelected(
    index: number,
    conditionType: SeminarConditionType
  ): void {
    if (['logo', 'vdb', 'vdp', 'bed'].includes(conditionType)) {
      this.participants.controls[index]
        .get('password')
        .setValidators([Validators.required]);
    } else {
      this.participants.controls[index].get('password').clearValidators();
    }
    this.participants.controls[index].get('password').updateValueAndValidity();
    if (conditionType === SeminarConditionType.OFFICE) {
      this.groupConditionSelected = true;
      const participantLength = this.participants.length;
      // add new participants so that there are at least 3 participants
      for (let i = 0; i < 3 - participantLength; i++) {
        this.addParticipant();
      }
      // set the condition for all participants to group
      this.participants.controls.forEach(participant => {
        participant.get('condition').setValue(SeminarConditionType.OFFICE);
      });
      this.cdr.detectChanges();
    } else {
      // Check if any participant still has office condition selected
      const hasOfficeCondition = this.participants.controls.some(
        participant =>
          participant.get('condition').value === SeminarConditionType.OFFICE
      );
      if (!hasOfficeCondition) {
        this.groupConditionSelected = false;
      }
    }
  }

  public onPaymentMethodSelected(paymentMethod: string) {
    this.selectedPaymentMethod = paymentMethod;

    if (paymentMethod === 'invoice') {
      this.currentScreen = 'paymentDetails';
      return;
    }

    if (paymentMethod === 'paypal') {
      if (this.bookingId) {
        this.router.navigate(['success', btoa(String(this.bookingId))], {
          relativeTo: this.activatedRoute,
        });
      }
    }
  }

  public onCompleteOrder(isFree: boolean = false): void {
    if (isFree) {
      this.selectedPaymentMethod = 'invoice';
    }
    if (this.bookingId) {
      this.updateBooking();
    } else {
      this.createBooking();
    }
  }

  private navigateAwayFromDraftSeminars(): boolean {
    if (this.seminar.isDraft) {
      this.router.navigate(['/']);
      this.alertService.showInfoAlert(
        'Das Seminar, dass du aufgerufen hast ist leider nicht mehr verfügbar.',
        10000
      );
      return true;
    }
    return false;
  }

  public async onSubmit(): Promise<void> {
    if (this.navigateAwayFromDraftSeminars()) {
      return;
    }
    this.isLoading = true;
    if (this.seminarRegistrationForm.invalid) {
      this.formService.markFormGroupTouched(this.seminarRegistrationForm);
      this.alertService.showErrorAlert(
        'Du hast das Formular noch nicht vollständig ausgefüllt!'
      );
      this.isLoading = false;
      return;
    }
    const bedNumberVerification = await this.verifyBedNumbers();
    if (!bedNumberVerification) {
      this.alertService.showErrorAlert(
        'Bei der Überprüfung der BED Mitgliedsnummer ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
      );
      this.isLoading = false;
      return;
    }
    if (bedNumberVerification && !bedNumberVerification?.success) {
      this.alertService.showErrorAlert(
        `Die BED Mitgliedsnummer für <strong><em>${bedNumberVerification?.name}</em></strong> ist nicht korrekt.`
      );
      this.isLoading = false;
      return;
    }
    const logoPasswordVerification = await this.verifyPasswords('logo');
    if (!logoPasswordVerification) {
      this.alertService.showErrorAlert(
        'Bei der Überprüfung des LOGO Deutschland Passworts ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
      );
      this.isLoading = false;
      return;
    }
    if (!logoPasswordVerification?.success) {
      this.alertService.showErrorAlert(
        `Das LOGO Deutschland Passwort für <strong><em>${logoPasswordVerification?.name}</em></strong> ist nicht korrekt.`
      );
      this.isLoading = false;
      return;
    }
    const vdbPasswordVerification = await this.verifyPasswords('vdb');
    if (!vdbPasswordVerification) {
      this.alertService.showErrorAlert(
        'Bei der Überprüfung des VDB Passworts ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
      );
      this.isLoading = false;
      return;
    }
    if (!vdbPasswordVerification?.success) {
      if (vdbPasswordVerification?.error) {
        switch (vdbPasswordVerification?.error) {
          case 'password_expired':
            this.alertService.showErrorAlert(
              `Das eingegebene VDB Passwort für <strong><em>${vdbPasswordVerification?.name}</em></strong> ist abgelaufen.`
            );
            break;
          default:
            this.alertService.showErrorAlert(
              `Das eingegebene VDB Passwort für <strong><em>${vdbPasswordVerification?.name}</em></strong> ist nicht korrekt.`
            );
            break;
        }
      }
      this.alertService.showErrorAlert(
        `Das eingegebene VDB Passwort für <strong><em>${vdbPasswordVerification?.name}</em></strong> ist nicht korrekt.`
      );
      this.isLoading = false;
      return;
    }
    const vdpPasswordVerification = await this.verifyPasswords('vdp');
    if (!vdpPasswordVerification) {
      this.alertService.showErrorAlert(
        'Bei der Überprüfung des VDP Passworts ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
      );
      this.isLoading = false;
      return;
    }
    if (!vdpPasswordVerification?.success) {
      if (vdpPasswordVerification?.error === 'password_expired') {
        this.alertService.showErrorAlert(
          `Das eingegebene VDP Passwort für <strong><em>${vdpPasswordVerification?.name}</em></strong> ist abgelaufen.`
        );
      } else {
        this.alertService.showErrorAlert(
          `Das eingegebene VDP Passwort für <strong><em>${vdpPasswordVerification?.name}</em></strong> ist nicht korrekt.`
        );
      }
      this.isLoading = false;
      return;
    }
    this.booking = {
      id: this.bookingId,
      bookingNr: null,
      seminar: {
        id: this.seminar.id,
        title: this.seminar.title,
        conditions: this.seminar.conditions,
        category: this.seminar.category1,
      },
      seminarDateSeries: this.seminar.dateSeries.find(
        it => it.id === this.seminarRegistrationForm.value.dateSeriesChoice
      ),
      totalPrice: this.calculatePrice(),
      discountedPrice: this.calculateDiscountedPrice(true),
      user: this.userService.currentUser$.value,
      status: null,
      participants: this.participants.value?.map(
        (participant: any): CourseParticipantModel => {
          return {
            id: participant.id,
            salutation: participant.salutation,
            jobTitle: participant.jobTitle,
            firstName: participant.firstName,
            lastName: participant.lastName,
            email: participant.email,
            street: participant.street,
            streetNr: participant.streetNr,
            zip: participant.zip,
            city: participant.city,
            country: participant.country,
            birthdate: participant.birthdate,
            questions: participant.questions,
            condition: participant.condition,
            conditionPrice: this.seminar.conditions.find(
              condition => condition.type === participant.condition
            )?.price,
          };
        }
      ),
      coupons: this.validCoupons,
      invoice: {
        id: null,
        firstName: this.seminarRegistrationForm.value.billingFirstName,
        lastName: this.seminarRegistrationForm.value.billingLastName,
        email: this.seminarRegistrationForm.value.billingEmail,
        street: this.seminarRegistrationForm.value.billingStreet,
        streetNr: this.seminarRegistrationForm.value.billingStreetNr,
        zip: this.seminarRegistrationForm.value.billingZip,
        city: this.seminarRegistrationForm.value.billingCity,
        country: this.seminarRegistrationForm.value.billingCountry,
        paymentMethod: null,
      },
      createdAt: null,
    };
    this.isLoading = false;
    this.currentScreen = 'registrationSummary';
  }

  private async verifyBedNumbers(): Promise<{
    success: boolean;
    name?: string;
  }> {
    const bedMembers = this.participants.controls
      .filter(
        participant =>
          participant.get('condition').value === SeminarConditionType.BED
      )
      .map(participant => ({
        bedNumber: participant.get('password').value,
        firstName: this.seminarRegistrationForm.get('billingFirstName').value,
        lastName: this.seminarRegistrationForm.get('billingLastName').value,
      }));
    if (bedMembers.length === 0) {
      return { success: true };
    }
    for (const bedMember of bedMembers) {
      try {
        const validation = await lastValueFrom(
          this.partnerService.validateBedMember(
            bedMember.firstName,
            bedMember.lastName,
            bedMember.bedNumber
          )
        );

        if (!validation) {
          return {
            success: false,
            name: `${bedMember.firstName} ${bedMember.lastName}`,
          };
        }
      } catch (error) {
        return null;
      }
    }
    return { success: true };
  }

  private async verifyPasswords(condition: string): Promise<{
    success: boolean;
    name?: string;
    error?: string;
  }> {
    const members = this.participants.controls
      .filter(participant => participant.get('condition').value === condition)
      .map(participant => ({
        password: participant.get('password').value,
        firstName: this.seminarRegistrationForm.get('billingFirstName').value,
        lastName: this.seminarRegistrationForm.get('billingLastName').value,
      }));
    if (members.length === 0) {
      return { success: true };
    }
    for (const member of members) {
      try {
        const validation = await lastValueFrom(
          this.partnerService.validatePassword(condition, member.password)
        );

        if (!validation) {
          return {
            success: false,
            name: `${member.firstName} ${member.lastName}`,
            error: validation.message,
          };
        }
      } catch (error) {
        return {
          success: false,
          name: `${member.firstName} ${member.lastName}`,
          error: error.message,
        };
      }
    }
    return { success: true };
  }

  private generateBookingCreateData(): void {
    this.bookingCreateModel = {
      id: this.bookingId,
      id_seminar_date_series:
        this.seminarRegistrationForm.value.dateSeriesChoice,
      participants: this.seminarRegistrationForm.value.participants?.map(
        (participant: any): CourseParticipantCreateUpdateModel => {
          return {
            id: participant.id || null,
            salutation: participant.salutation,
            job_title: participant.jobTitle,
            first_name: participant.firstName,
            last_name: participant.lastName,
            email: participant.email,
            phone_number: participant.phone,
            street: participant.street,
            street_nr: participant.streetNr,
            zip: participant.zip,
            city: participant.city,
            country: participant.country,
            birthdate: moment(participant.birthdate).format('YYYY-MM-DD'),
            questions: participant.questions,
            condition: participant.condition,
            condition_price: this.seminar.conditions.find(
              condition => condition.type === participant.condition
            )?.price,
          };
        }
      ),
      invoice_address: {
        facility: this.seminarRegistrationForm.value.billingFacility,
        first_name: this.seminarRegistrationForm.value.billingFirstName,
        last_name: this.seminarRegistrationForm.value.billingLastName,
        email: this.seminarRegistrationForm.value.billingEmail,
        phone_number: this.seminarRegistrationForm.value.billingPhone,
        street: this.seminarRegistrationForm.value.billingStreet,
        street_nr: this.seminarRegistrationForm.value.billingStreetNr,
        zip: this.seminarRegistrationForm.value.billingZip,
        city: this.seminarRegistrationForm.value.billingCity,
        country: this.seminarRegistrationForm.value.billingCountry,
      },
      id_coupons: this.validCoupons.map(coupon => coupon.id),
      total_price: this.calculatePrice(),
      discounted_price: this.calculateDiscountedPrice(),
      payment_method: this.selectedPaymentMethod,
    };
  }

  private createBooking(): void {
    if (this.navigateAwayFromDraftSeminars()) {
      return;
    }
    this.isLoading = true;
    this.generateBookingCreateData();
    this.bookingService
      .createBooking(this.bookingCreateModel)
      .pipe(take(1))
      .subscribe({
        next: (booking: any) => {
          this.isLoading = false;

          this.router.navigate(['success', btoa(String(booking.booking_id))], {
            relativeTo: this.activatedRoute,
          });
        },
        error: error => {
          this.isLoading = false;
          if (
            error.status === 409 &&
            error.error.startsWith('user_already_registered')
          ) {
            const email = error.error.split(':')[1]?.trim();
            this.alertService.showErrorAlert(
              `Ein Benutzer mit der E-Mail-Adresse <strong><em>${email}</em></strong> ist bereits für diesen Kurs angemeldet.`,
              10000
            );
            return;
          }
          if (error.error === 'not_enough_seats') {
            this.alertService.showErrorAlert(
              'Der Kurs hat nicht mehr genug freie Plätze.'
            );
          } else {
            this.alertService.showErrorAlert(
              'Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
            );
          }
        },
      });
  }

  private updateBooking(): void {
    if (this.navigateAwayFromDraftSeminars()) {
      return;
    }
    this.isLoading = true;
    this.generateBookingCreateData();
    this.bookingService
      .updateBooking(this.bookingCreateModel)
      .pipe(take(1))
      .subscribe({
        next: (booking: any) => {
          this.isLoading = false;

          this.router.navigate(
            ['../../success', btoa(String(booking.booking_id))],
            {
              relativeTo: this.activatedRoute,
            }
          );
        },
        error: error => {
          this.isLoading = false;
          if (error.error === 'not_enough_seats') {
            this.alertService.showErrorAlert(
              'Der Kurs hat leider schon wieder keine freien Plätze mehr.'
            );
          } else {
            this.alertService.showErrorAlert(
              'Es ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
            );
          }
        },
      });
  }

  public onGoToCheckout(): void {
    if (this.navigateAwayFromDraftSeminars()) {
      return;
    }
    this.generateBookingCreateData();
    this.currentScreen = 'paymentMethod';
  }

  public onGoBack(): void {
    if (this.currentScreen === 'registrationSummary') {
      this.currentScreen = 'registration';
      return;
    }
    if (this.currentScreen === 'paymentMethod') {
      this.currentScreen = 'registrationSummary';
      return;
    }
    if (this.currentScreen === 'paymentDetails') {
      this.currentScreen = 'paymentMethod';
      return;
    }
    this.location.back();
  }

  public freeSeatsText(dateSeries: SeminarDateSeriesModel): string {
    const maxParticipants = this.seminar?.maxParticipants;
    const participantCount = dateSeries.participantCount;

    const freeSeats = maxParticipants - participantCount;

    if (freeSeats <= 0) {
      return 'Warteliste';
    }
    if (freeSeats === 1) {
      return '1 Platz';
    }
    return `${freeSeats} Plätze`;
  }

  public freeSeats(): number {
    const maxParticipants = this.seminar?.maxParticipants;
    const participantCount = this.seminar?.dateSeries.find(
      it => it.id === this.seminarRegistrationForm.value.dateSeriesChoice
    )?.participantCount;

    return maxParticipants - participantCount;
  }

  public async onBookWaitlist(): Promise<void> {
    if (this.navigateAwayFromDraftSeminars()) {
      return;
    }
    this.isLoading = true;
    if (this.seminarRegistrationForm.invalid) {
      this.formService.markFormGroupTouched(this.seminarRegistrationForm);
      this.alertService.showErrorAlert(
        'Du hast das Formular noch nicht vollständig ausgefüllt!'
      );
      this.isLoading = false;
      return;
    }
    const bedNumberVerification = await this.verifyBedNumbers();
    if (!bedNumberVerification) {
      this.alertService.showErrorAlert(
        'Bei der Überprüfung der BED Mitgliedsnummer ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
      );
      this.isLoading = false;
      return;
    }
    if (!bedNumberVerification?.success) {
      this.alertService.showErrorAlert(
        `Die BED Mitgliedsnummer für <strong><em>${bedNumberVerification?.name}</em></strong> ist nicht korrekt.`
      );
      this.isLoading = false;
      return;
    }
    this.generateBookingCreateData();
    this.bookingService
      .createWaitlistBooking(this.bookingCreateModel)
      .subscribe({
        next: response => {
          this.alertService.showSuccessAlert(
            'Du wurdest erfolgreich auf die Warteliste gesetzt.'
          );
          this.seminar.dateSeries.find(
            it => it.id === this.seminarRegistrationForm.value.dateSeriesChoice
          ).currentUserStatus = 'waitlist';
          this.isLoading = false;
          // scroll to top
          this.location.back();
        },
        error: error => {
          this.isLoading = false;
          console.error('Error creating booking waitlist:', error);

          if (
            error.status === 409 &&
            error.error.startsWith('user_already_registered')
          ) {
            const email = error.error.split(':')[1]?.trim();
            this.alertService.showErrorAlert(
              `Ein Benutzer mit der E-Mail-Adresse <strong><em>${email}</em></strong> ist bereits für diesen Kurs angemeldet.`,
              10000
            );
            return;
          }
          this.alertService.showErrorAlert(
            'Bei der Anmeldung ist ein Fehler aufgetreten. Bitte versuche es später noch einmal.'
          );
        },
      });
  }

  public isOnWaitlist(): boolean {
    return (
      this.seminar?.dateSeries.find(
        it => it.id === this.seminarRegistrationForm.value.dateSeriesChoice
      ).currentUserStatus === 'waitlist'
    );
  }

  public subscribeToCouponCodeInput(): void {
    this.couponSubscription = this.seminarRegistrationForm
      .get('couponCode')
      .valueChanges.pipe(debounceTime(500))
      .subscribe({
        next: value => {
          this.alertService.hideAlert();
          if (!value) {
            return;
          }
          this.validateCouponCode(value);
        },
      });
  }

  private validateCouponCode(code: string, showErrorAlert = false) {
    this.couponService
      .validateCouponCode(code)
      .pipe(take(1))
      .subscribe({
        next: couponCode => {
          if (this.validCoupons.find(coupon => coupon.id === couponCode.id)) {
            if (
              this.validCoupons.findIndex(
                coupon => coupon.id === couponCode.id
              ) !==
              this.validCoupons.length - 1
            ) {
              this.alertService.showErrorAlert(
                'Der Gutscheincode wurde bereits eingelöst.'
              );
            }
            this.seminarRegistrationForm
              .get('couponCode')
              .setErrors({ alreadyUsedCouponCode: true });
            this.seminarRegistrationForm.get('couponCode').markAsTouched();
            return;
          }

          if (
            couponCode.type === 'promo' &&
            this.validCoupons.filter(coupon => coupon.type === 'promo')
              ?.length > 0
          ) {
            this.alertService.showErrorAlert(
              'Es kann nur ein Promocode verwendet werden.'
            );
            return;
          }

          // Check seminar restrictions
          if (
            couponCode.allowedSeminarIds &&
            couponCode.allowedSeminarIds.length > 0 &&
            this.seminar
          ) {
            const isInList = couponCode.allowedSeminarIds.includes(
              this.seminar.id
            );
            const isInvalid = couponCode.seminarInverted ? isInList : !isInList;

            if (isInvalid) {
              this.alertService.showErrorAlert(
                couponCode.seminarInverted
                  ? 'Dieser Gutscheincode ist für dieses Seminar <strong>ausgeschlossen</strong>.'
                  : 'Dieser Gutscheincode ist für dieses Seminar <strong>nicht</strong> gültig.'
              );
              this.seminarRegistrationForm
                .get('couponCode')
                .setErrors({ invalidForSeminar: true });
              this.seminarRegistrationForm.get('couponCode').markAsTouched();
              return;
            }
          }

          // Check condition type restrictions
          if (
            couponCode.allowedConditionTypes &&
            couponCode.allowedConditionTypes.length > 0 &&
            this.participants
          ) {
            const participantConditions = this.participants.controls
              .map(control => control.get('condition').value)
              .filter(condition => condition !== null);

            const hasInvalidCondition = participantConditions.some(
              condition => !couponCode.allowedConditionTypes.includes(condition)
            );

            if (hasInvalidCondition) {
              this.alertService.showErrorAlert(
                'Dieser Gutscheincode ist für die ausgewählten Preis-Kategorien <strong>nicht</strong> gültig.'
              );
              this.seminarRegistrationForm
                .get('couponCode')
                .setErrors({ invalidForConditionType: true });
              this.seminarRegistrationForm.get('couponCode').markAsTouched();
              return;
            }
          }

          if (
            this.calculateDiscountedPrice(true) < 0 &&
            couponCode.type === 'sellable'
          ) {
            this.seminarRegistrationForm
              .get('couponCode')
              .setErrors({ alreadyNegative: true });
            this.seminarRegistrationForm.get('couponCode').markAsTouched();
            return;
          }
          this.validCoupons.push(couponCode);
          this.showCouponInput = false;
          this.seminarRegistrationForm.get('couponCode').setValue('');
        },
        error: error => {
          this.seminarRegistrationForm
            .get('couponCode')
            .setErrors({ invalidCouponCode: true });
          this.seminarRegistrationForm.get('couponCode').markAsTouched();
          if (showErrorAlert) {
            const errorMessage =
              error.error === 'coupon_not_valid_for_seminar'
                ? 'Dieser Gutscheincode ist für dieses Seminar <strong>nicht</strong> gültig.'
                : error.error === 'coupon_not_valid_for_price_category'
                  ? 'Dieser Gutscheincode ist für die ausgewählten Preis-Kategorien <strong>nicht</strong> gültig.'
                  : 'Der Gutscheincode konnte nicht überprüft werden.';
            this.alertService.showErrorAlert(errorMessage);
          }
        },
      });
  }

  public onCouponCodeKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
      this.couponSubscription.unsubscribe();
      this.validateCouponCode(event.target.value, true);
      this.subscribeToCouponCodeInput();
    }
  }

  public removeCoupon(index: number): void {
    this.validCoupons.splice(index, 1);
    if (this.validCoupons.length === 0) {
      this.showCouponInput = true;
    }
  }

  public priceSelectionValid(): boolean {
    return this.participants.controls.every(
      participant => participant.get('condition').value !== null
    );
  }
}
